<template>
  <button
    v-bind="$attrs"
    class="brand-btn primary-font"
    :class="[theme, color, size,
      borderRadius? 'border-radius-button' : '',
      !bold ?  'label-md-700' : 'label-md-400',
      capslock? 'text-uppercase': '',
      selected ? 'selected-border' : '',
    ]"
    type="button"
    :tabindex="props.disabled ? -1 : 0"
    :ariad-label="props.disabled ? 'disabled' : 'button'"
    :disabled="props.disabled"
    :aria-disabled="props.disabled"
  >
  <div :class="fontColor">
    <slot />
  </div>
  </button>
</template>

<script setup lang="ts">
import { type TypeTheme, Theme, type TypeColor, Color, type TypeSize, Size } from '../../interfaces'

export interface PropsButton {
  theme?: TypeTheme
  color?: TypeColor
  fontColor?: TypeColor
  size?: TypeSize
  bold?: boolean
  borderRadius?: boolean
  capslock?: boolean
  disabled?: boolean
  alignRight?: boolean
  selected?: boolean
}
const props = withDefaults(defineProps<PropsButton>(), {
  theme: Theme.primary,
  capslock: true,
  color: Color.cork,
  fontColor: Color.black,
  size: Size.md,
  bg: true,
  bold: true,
  border: true,
  borderRadius: true,
  alignRight: false,
  selected: false
})

</script>


<style lang="scss" scoped>
$sun: var( --button-primary-sun-background-color, '#FFA300');
$sunHover: var( --button-primary-sun-background-hover, '#DF9C06');
$focusColor: var( --button-primary-sun-border-focus, '#393939');
$cork: var(  --button-primary-cork-background-color, '#393939');
$corkHover: var(  --button-primary-cork-background-hover, '#393939');
$corkDisable: var(  --button-primary-cork-background-disable, '#FFF9ED');
$pink: var(  --button-primary-pink-background-color, '#F7C1B7');
$pinkHover: var(  --button-primary-pink-background-hover, '#FFEAE7');
$white: var(  --button-primary-white-background-color, '#F7C1B7');
$whiteHover: var(  --button-primary-white-background-hover, '#FFEAE7');
$textShadow:  1px 1px 1px var( --button-primary-text-shadow-color, '#393939');
$whiteFont: var( --button-white-font-color, '#FFFFFF');
$blackFont: var( --button-black-font-color, '#53565A');
$paddingButtonMd: var(--padding-button-md, 2.5rem);
$paddingButtonSm: var(--padding-button-sm, 1.25rem);
$paddingButtonXS: var(--padding-button-sm, 0.6125rem);
$borderRadiusButton: var(--border-radius-button, 25rem);
$outline: 0.125rem;
@media (min-width: 769px) {
    .brand-btn-standard  {
        padding: $paddingButtonSm $paddingButtonMd;
    }
}
@supports not selector(:focus-visible) {
    .brand-btn:focus {
      border-color:   transparent;
    }
  }
button:disabled {
  cursor: not-allowed;
}
.border-radius-button {
  border-radius: $borderRadiusButton;
}
.brand-btn {
  box-sizing: border-box;
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  // min-height: 4rem;
  min-height: 3.5rem;
  border-width: $outline;
  text-shadow: none;
  border-style: solid;
  display: inline-block;
  outline: 0;

  >.black {
    color: $blackFont;
  }
  >.white {
    color: $whiteFont;
  }
  &:hover {
    // border-color:   rgba(0, 0, 0, 0);
    text-decoration: none;
  }
  &.primary {
    border-style: solid;
    border-color: transparent;
  }

  &.primary:disabled {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.4;
  }

  &.primary:disabled:hover {
    background-color: none;
  }

  &.transparent {
    background-color: transparent;
    border-color: transparent;
  }


  // &:focus {
  //   box-shadow: -0.125rem 0.250rem 0.5rem -0.25rem var(--button-primary-white-background-color, #FFFFFF);
  // }
  &:active {
    border-color:  none;
  }
  &:visited {
    border-color:   none;
  }

  &.primary.sun {
    background-color: $sun;
    color: $whiteFont;
    border-color:  $sun;
    // text-shadow: $textShadow;
  }
  &.sun:hover {
    background-color: $sunHover;
  }
  &.sun:active {
    text-shadow: none;
  }

  &.sun:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &.none {
    background-color: transparent;
    border: transparent solid 0px;
    color: $blackFont;
    padding: 0;
    text-transform: none;

  }

  &.primary.cork {
    background-color: $cork;
  }
  &.primary.cork:hover {
    background-color: $corkHover;
  }
  &.cork:disabled {
    background-color: $corkDisable;
    cursor: not-allowed;
  }

  &.primary.pink {
    background-color: $pink;
  }
  // &.pink:disabled {
  // }
  &.pink:hover {
    background-color: $pinkHover;
  }
  &.primary.white {
    background-color: $white;
  }
  &.primary.white:hover {
    background-color: $whiteHover;
  }
  &.white:focus {
    border-color: $sun;
  }

  @supports not selector(:focus-visible) {
    &.white:focus {
      border-color:   none;
    }
  }

  &.secondary {
    background-color: rgba(0, 0, 0, 0);
    // color: var( --button-secondary-font-color, '#000');
    // border-color: var( --button-secondary-background-color, '#000');
  }


  &.secondary.white {
   border-color: $white;
   border-width: $outline;
   color: $blackFont!important;
  }
  &.secondary.white:hover {
  background-color: none!important;
  //  background-color: $white;
   border-color: $white;
   border-width: $outline;
   color: $blackFont!important;
   padding: calc($outline * 2);
  }
  &.secondary.white:focus {
   background-color: $sun;
   border-color: $white;
   border-width: $outline;
   color: $blackFont!important;
   padding: calc($outline * 2);
  }

  &.primary.sun:focus {
   border-color: $white;
   border-width: $outline;
   text-shadow: none;
  }
  &.primary.with:focus {
   border-color: $blackFont;
   border-width: $outline;
   text-shadow: none;
  }

  &.md {
    padding: 0 $paddingButtonMd 0 $paddingButtonMd;
    min-width: 9.5rem;
    @media (max-width: 320px) {
      max-width: 9.5rem;
    }
    @media screen and (max-width: 480px) {
      padding: 0 $paddingButtonSm 0 $paddingButtonSm;
    }
    @media screen and (max-width: 768px)  and (orientation: landscape) {
      padding: 0 $paddingButtonSm 0 $paddingButtonSm;
    }
  }

  &.xs {
    min-width: fit-content;
    border-radius: 3rem;
    max-height: 3.5rem;
    padding: 0 $paddingButtonMd 0 $paddingButtonMd;
    @media screen and (max-width: 480px) {
      padding: 0 $paddingButtonSm 0 $paddingButtonSm;
    }
  }
  &.xxl {
    width: 100%;
    padding: 0 $paddingButtonMd 0 $paddingButtonMd;
    @media screen and (max-width: 480px) {
      padding: 0 $paddingButtonSm 0 $paddingButtonSm;
    }
  }
  // &.secondary:hover {
  //   // background-color: var( --button-secondary-background-hover, '#DF9C06');
  // }
}
.brand-btn-xs {
  @extend .brand-btn;

}
</style>
